import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useCart } from '@/hooks/useCart';

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [shopOpen, setShopOpen] = useState(false);
  const { cartCount } = useCart();
  const location = useLocation();
  const shopRef = useRef<HTMLDivElement>(null);

  // Pop the cart badge whenever the item count goes UP (item added). Replays the
  // CSS keyframe by toggling a class for ~450ms.
  const [badgePop, setBadgePop] = useState(false);
  const prevCount = useRef(cartCount);
  useEffect(() => {
    if (cartCount > prevCount.current) {
      setBadgePop(true);
      const t = setTimeout(() => setBadgePop(false), 450);
      prevCount.current = cartCount;
      return () => clearTimeout(t);
    }
    prevCount.current = cartCount;
  }, [cartCount]);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setSearchOpen(false);
    setShopOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (shopRef.current && !shopRef.current.contains(e.target as Node)) {
        setShopOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const announcement = "70+ Wind Spinner Designs · Ships from the US · Handcrafted Quality";

  return (
    <>
      {/* Announcement Bar */}
      <div className="bg-[#2c2a4a] text-white text-center py-2.5 px-4 text-xs md:text-sm font-sans">
        <p>{announcement}</p>
      </div>

      {/* Main Header */}
      <header
        className={`sticky top-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-white shadow-sm border-b border-[#E8E0D4]' : 'bg-white/95 backdrop-blur-sm'
        }`}
      >
        <div className="container-main">
          <div className="flex items-center justify-between h-[88px] md:h-[104px]">
            {/* Logo */}
            <Link to="/" className="flex items-center shrink-0">
              <img src="https://static.readdy.ai/image/577c45d6defa6ac02f4bf39c73856fa0/e390834940d0c940fbae12edf468638d.png" alt="SpinWorld" className="w-[90px] md:w-[110px] max-h-[64px] md:max-h-[80px] h-auto object-contain" />
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-8">
              <Link to="/" className="text-sm font-sans font-medium text-[#1A1A1A] hover:text-[#4f528d] transition-colors uppercase tracking-wider">Home</Link>
              <div ref={shopRef} className="relative">
                <button
                  onClick={() => setShopOpen(!shopOpen)}
                  className="flex items-center gap-1 text-sm font-sans font-medium text-[#1A1A1A] hover:text-[#4f528d] transition-colors uppercase tracking-wider"
                >
                  Shop
                  <i className={`ri-arrow-down-s-line transition-transform ${shopOpen ? 'rotate-180' : ''}`} />
                </button>
                {shopOpen && (
                  <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-56 bg-white rounded-lg shadow-xl border border-[#E8E0D4] overflow-hidden py-2 z-50">
                    <Link to="/shop" className="block px-4 py-2.5 text-sm text-[#1A1A1A] hover:bg-[#d1bedd] hover:text-[#4f528d] transition-colors">Shop All</Link>
                    <div className="border-t border-[#E8E0D4] my-1" />
                    <Link to="/collections/wind-spinners" className="block px-4 py-2.5 text-sm text-[#1A1A1A] hover:bg-[#d1bedd] hover:text-[#4f528d] transition-colors">Wind Spinners</Link>
                    <Link to="/collections/soapstone-animals" className="block px-4 py-2.5 text-sm text-[#1A1A1A] hover:bg-[#d1bedd] hover:text-[#4f528d] transition-colors">Soapstone Animals</Link>
                    <Link to="/collections/wire-wrapped-stones" className="block px-4 py-2.5 text-sm text-[#1A1A1A] hover:bg-[#d1bedd] hover:text-[#4f528d] transition-colors">Wire Wrapped Stones</Link>
                    <Link to="/collections/accessories" className="block px-4 py-2.5 text-sm text-[#1A1A1A] hover:bg-[#d1bedd] hover:text-[#4f528d] transition-colors">Accessories</Link>
                  </div>
                )}
              </div>
              <Link to="/about" className="text-sm font-sans font-medium text-[#1A1A1A] hover:text-[#4f528d] transition-colors uppercase tracking-wider">About</Link>
              <Link to="/contact" className="text-sm font-sans font-medium text-[#1A1A1A] hover:text-[#4f528d] transition-colors uppercase tracking-wider">Contact</Link>
            </nav>

            {/* Actions */}
            <div className="flex items-center gap-3 md:gap-4">
              <button onClick={() => setSearchOpen(!searchOpen)} className="w-9 h-9 flex items-center justify-center text-[#1A1A1A] hover:text-[#4f528d] transition-colors">
                <i className="ri-search-line text-lg" />
              </button>
              <Link to="/cart" aria-label={`Cart, ${cartCount} item${cartCount === 1 ? '' : 's'}`} className="relative w-9 h-9 flex items-center justify-center text-[#1A1A1A] hover:text-[#4f528d] transition-colors">
                <i className={`ri-shopping-bag-line text-lg ${badgePop ? 'cart-bag-bump' : ''}`} />
                {cartCount > 0 && (
                  <span className={`absolute -top-0.5 -right-0.5 min-w-5 h-5 px-1 bg-[#B87333] text-white text-[10px] font-bold rounded-full flex items-center justify-center ${badgePop ? 'cart-badge-pop' : ''}`}>
                    {cartCount > 99 ? '99+' : cartCount}
                  </span>
                )}
              </Link>
              <button onClick={() => setMobileOpen(!mobileOpen)} className="md:hidden w-9 h-9 flex items-center justify-center text-[#1A1A1A]">
                <i className={`${mobileOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl`} />
              </button>
            </div>
          </div>
        </div>

        {/* Search Overlay */}
        {searchOpen && (
          <div className="absolute inset-x-0 top-full bg-white border-b border-[#E8E0D4] shadow-lg py-4 z-40">
            <div className="container-main">
              <form className="relative" onSubmit={(e) => { e.preventDefault(); }}>
                <input
                  autoFocus
                  type="text"
                  placeholder="Search wind spinners, soapstone, jewelry..."
                  className="w-full bg-[#FAF8F4] border border-[#D4C9B8] rounded-md py-3 pl-12 pr-4 text-sm font-sans text-[#1A1A1A] placeholder:text-[#8B7355] focus:outline-none focus:border-[#B87333]"
                />
                <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-[#8B7355]" />
                <button type="button" onClick={() => setSearchOpen(false)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8B7355] hover:text-[#1A1A1A]">
                  <i className="ri-close-line text-lg" />
                </button>
              </form>
            </div>
          </div>
        )}

        {/* Mobile Menu */}
        {mobileOpen && (
          <div className="md:hidden absolute inset-x-0 top-full bg-white border-b border-[#E8E0D4] shadow-lg z-40">
            <nav className="container-main py-4 space-y-1">
              <Link to="/" className="block py-3 text-sm font-medium text-[#1A1A1A] border-b border-[#E8E0D4] uppercase tracking-wider">Home</Link>
              <Link to="/shop" className="block py-3 text-sm font-medium text-[#1A1A1A] border-b border-[#E8E0D4] uppercase tracking-wider">Shop All</Link>
              <Link to="/collections/wind-spinners" className="block py-3 text-sm font-medium text-[#1A1A1A] border-b border-[#E8E0D4]">Wind Spinners</Link>
              <Link to="/collections/soapstone-animals" className="block py-3 text-sm font-medium text-[#1A1A1A] border-b border-[#E8E0D4]">Soapstone Animals</Link>
              <Link to="/collections/wire-wrapped-stones" className="block py-3 text-sm font-medium text-[#1A1A1A] border-b border-[#E8E0D4]">Wire Wrapped Stones</Link>
              <Link to="/collections/accessories" className="block py-3 text-sm font-medium text-[#1A1A1A] border-b border-[#E8E0D4]">Accessories</Link>
              <Link to="/about" className="block py-3 text-sm font-medium text-[#1A1A1A] border-b border-[#E8E0D4] uppercase tracking-wider">About</Link>
              <Link to="/contact" className="block py-3 text-sm font-medium text-[#1A1A1A] uppercase tracking-wider">Contact</Link>
            </nav>
          </div>
        )}
      </header>
    </>
  );
}